import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { FinanceClientPriceModelClient, FinanceClientPriceModelDTO, FinancePriceModelDataDTO, IFinanceClientPriceModelDTO } from './../../../../../base/models/models.swagger.generated';
import { Injectable } from '@angular/core';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ToastService } from 'src/app/base/services/toast.service';
import { ErrorType } from 'src/app/core/routing/error-types';
import { DateTime } from 'luxon';
import { DateHelper } from 'src/app/base/shared/date-helper';

export interface IExtendedFinanceClientPriceModelDTO extends IFinanceClientPriceModelDTO {
  validToDate?: DateTime | undefined;
}

@Injectable({
  providedIn: 'root'
})
export class FinanceClientPriceModelService {

  constructor(private priceModelClient: FinanceClientPriceModelClient,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
  ) { }

  public getPriceModel(clientId: number): Observable<IExtendedFinanceClientPriceModelDTO[]> {
    this.spinnerService.show();
    return this.priceModelClient.getFinanceClientPriceModels(clientId)
      .pipe(
        map((models: IFinanceClientPriceModelDTO[]) => {
          this.spinnerService.hide();
          let extendedModels: IExtendedFinanceClientPriceModelDTO[] = models as IExtendedFinanceClientPriceModelDTO[];
          extendedModels.forEach(model => {
            if (model.validTo) {
              model.validToDate = DateHelper.getDateFromIsoDateString(model.validTo);
            } else {
              model.validToDate = undefined;
            }
          });
          return extendedModels;
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  public updatePriceModel(data: IExtendedFinanceClientPriceModelDTO) {
    this.spinnerService.show();
    data.validTo = DateHelper.toIsoDateString(data.validToDate);
    return this.priceModelClient.updateFinanceClientPriceModel(new FinanceClientPriceModelDTO(data))
      .pipe(
        tap(() => {
          this.spinnerService.hide();
          this.toastService.showSuccess('i18n.domain.client-administration.price-model.title', 'i18n.domain.client-administration.price-model.update-success-message');
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  public createPriceModel(clientId: number, data: IExtendedFinanceClientPriceModelDTO) {
    this.spinnerService.show();
    data.validTo = DateHelper.toIsoDateString(data.validToDate);
    return this.priceModelClient.addFinanceClientPriceModel(clientId, new FinanceClientPriceModelDTO(data))
      .pipe(
        tap(() => {
          this.spinnerService.hide();
          this.toastService.showSuccess('i18n.domain.client-administration.price-model.title', 'i18n.domain.client-administration.price-model.add-success-message');
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  public getPriceModelMasterData(): Observable<FinancePriceModelDataDTO> {
    this.spinnerService.show();
    return this.priceModelClient.getAllPriceModels()
      .pipe(
        tap(() => this.spinnerService.hide()),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  public deletePriceModel(priceModelId: number) {
    this.spinnerService.show();
    return this.priceModelClient.deleteFinanceClientPriceModel(priceModelId)
      .pipe(
        tap(() => {
          this.spinnerService.hide();
          this.toastService.showSuccess('i18n.domain.client-administration.price-model.title', 'i18n.domain.client-administration.price-model.delete-success-message');
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

}
